import type { SchemaInput } from '../shared/schema-input.js';
import { haversineKm } from './distance-math.js';

export const MAX_ROUTE_SNAP_DISTANCE_M = 1_000;
export const OSRM_ENDPOINT = {
  driving: 'https://router.project-osrm.org/route/v1/driving',
  walking: 'https://routing.openstreetmap.de/routed-foot/route/v1/foot',
  cycling: 'https://routing.openstreetmap.de/routed-bike/route/v1/bike',
};

export function routeWaypointsAreNearby(value: SchemaInput, requested: Array<{ lat: number; lng: number }>): boolean {
  return Array.isArray(value) && value.length === requested.length && value.every((waypoint, index) => {
    const coordinate: SchemaInput = waypoint?.location;
    return isRouteCoordinate(coordinate)
      && haversineKm(requested[index].lat, requested[index].lng, coordinate[1], coordinate[0]) * 1000 <= MAX_ROUTE_SNAP_DISTANCE_M;
  });
}

export function isRouteCoordinate(value: SchemaInput): value is [number, number] {
  return Array.isArray(value) && value.length === 2
    && Number.isFinite(value[0]) && Math.abs(value[0]) <= 180
    && Number.isFinite(value[1]) && Math.abs(value[1]) <= 90;
}

const hosts = new Map<string, { ready: Promise<void>; lastStarted: number }>();

function waitForRequest(delay: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(signal?.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, delay);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export async function fetchOsrm(url: string, signal?: AbortSignal): Promise<Response> {
  signal?.throwIfAborted();
  const host = new URL(url).host;
  let queue = hosts.get(host);
  if (!queue) {
    queue = { ready: Promise.resolve(), lastStarted: -Infinity };
    hosts.set(host, queue);
  }
  const start = queue.ready.then(async () => {
    signal?.throwIfAborted();
    const delay = Math.max(0, Math.ceil(queue.lastStarted + 1_000 - performance.now()));
    if (delay > 0) await waitForRequest(delay, signal);
    signal?.throwIfAborted();
    queue.lastStarted = performance.now();
  });
  queue.ready = start.catch(() => {});
  await start;
  return fetch(url, { signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(30_000)]) : AbortSignal.timeout(30_000) });
}
